// ─── Auth API Endpoints ───────────────────────────────────────────
const BASE = '/auth';
const INTEGRATIONS_BASE = '/integrations';

export const AUTH_ENDPOINTS = {
    LOGIN: `${BASE}/login`,
    REGISTER: `${BASE}/register`,
    LOGOUT: `${BASE}/logout`,
    ME: `${BASE}/me`,
    REFRESH: `${BASE}/refresh`,
    SWITCH_CONTEXT: `${BASE}/switch-context`,
    FORGOT_PASSWORD: `${BASE}/forgot-password`,
    RESET_PASSWORD: `${BASE}/reset-password`,
    VALIDATE_INVITE: `${BASE}/invitations/validate`,
    ACCEPT_INVITE: `${BASE}/invitations/accept`,
    GOOGLE_INITIATE: `${BASE}/google/initiate`,
    GOOGLE_CALLBACK: `${BASE}/google/callback`,
    MAGIC_LINK_REQUEST: `${BASE}/magic-link/request`,
    MAGIC_LINK_VERIFY: `${BASE}/magic-link/verify`,
    VERIFY_EMAIL: `${BASE}/verify-email`,
    RESEND_VERIFICATION: `${BASE}/verify-email/resend`,
    SHOPIFY_SETUP_DATA: `${BASE}/shopify-setup-data`,
    COMPLETE_SHOPIFY_SETUP: `${BASE}/complete-shopify-setup`,
} as const;

export const INTEGRATION_ENDPOINTS = {
    SHOPIFY_INITIATE: `${INTEGRATIONS_BASE}/shopify/initiate`,
    SHOPIFY_CALLBACK: `${INTEGRATIONS_BASE}/shopify/callback`,
    SHOPIFY_DISCONNECT: (storeId: string) =>
        `${INTEGRATIONS_BASE}/shopify/${storeId}`,
    AVAILABLE: `${INTEGRATIONS_BASE}/available`,
} as const;

// ─── Auth Status (State Machine) ──────────────────────────────────
export const AUTH_STATUS = {
    INITIALIZING: 'initializing',
    AUTHENTICATED: 'authenticated',
    UNAUTHENTICATED: 'unauthenticated',
    MIGRATING: 'migrating',
} as const;

export type AuthStatusType = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS];

// ─── Session Types ────────────────────────────────────────────────
export const SESSION_TYPES = {
    DASHBOARD_USER: 'dashboard_user',
    LEGACY_SHOPIFY: 'legacy_shopify_redirect',
} as const;

export type SessionType = (typeof SESSION_TYPES)[keyof typeof SESSION_TYPES];

// ─── Auth Providers ───────────────────────────────────────────────
export const AUTH_PROVIDERS = {
    PASSWORD: 'password',
    GOOGLE: 'google',
    MAGIC_LINK: 'magic_link',
    SHOPIFY: 'shopify',
} as const;

export type AuthProviderType =
    (typeof AUTH_PROVIDERS)[keyof typeof AUTH_PROVIDERS];

// ─── RBAC Permissions (19 keys — single source of truth) ──────────
export const PERMISSIONS = {
    SURVEYS_READ: 'surveys.read',
    SURVEYS_WRITE: 'surveys.write',
    ANALYTICS_READ: 'analytics.read',
    RESPONSES_READ: 'responses.read',
    CHANNELS_READ: 'channels.read',
    CHANNELS_WRITE: 'channels.write',
    TARGETING_READ: 'targeting.read',
    TARGETING_WRITE: 'targeting.write',
    TEMPLATES_READ: 'templates.read',
    TEMPLATES_WRITE: 'templates.write',
    BILLING_READ: 'billing.read',
    BILLING_WRITE: 'billing.write',
    INTEGRATIONS_READ: 'integrations.read',
    INTEGRATIONS_WRITE: 'integrations.write',
    ONBOARDING_READ: 'onboarding.read',
    ONBOARDING_WRITE: 'onboarding.write',
    AI_READ: 'ai.read',
    AI_WRITE: 'ai.write',
    TEAM_MANAGE: 'team.manage',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Flat array of every permission key string. */
export const ALL_PERMISSION_KEYS: PermissionKey[] = Object.values(PERMISSIONS);

/**
 * Default permission sets per role.
 * When a role is assigned, the member receives these permissions.
 * org_owner and org_admin bypass checks entirely (super roles).
 */
export const ROLE_DEFAULTS: Record<string, PermissionKey[]> = {
    org_owner: [...ALL_PERMISSION_KEYS],
    org_admin: [...ALL_PERMISSION_KEYS],
    org_member: ALL_PERMISSION_KEYS.filter(
        (p) => !['billing.read', 'billing.write', 'team.manage'].includes(p)
    ),
    editor: ALL_PERMISSION_KEYS.filter(
        (p) => !['billing.read', 'billing.write', 'team.manage'].includes(p)
    ),
    analyst: [
        PERMISSIONS.SURVEYS_READ,
        PERMISSIONS.ANALYTICS_READ,
        PERMISSIONS.RESPONSES_READ,
        PERMISSIONS.TEMPLATES_READ,
        PERMISSIONS.AI_READ,
    ],
    viewer: [PERMISSIONS.SURVEYS_READ, PERMISSIONS.ANALYTICS_READ, PERMISSIONS.TEMPLATES_READ],
    org_viewer: [PERMISSIONS.SURVEYS_READ, PERMISSIONS.ANALYTICS_READ, PERMISSIONS.TEMPLATES_READ],
};

// ─── Super Roles (bypass all permission checks) ───────────────────
export const SUPER_ROLES = ['org_owner', 'org_admin', 'super_admin'] as const;

// ─── Error Message Mapping ────────────────────────────────────────
export const ERROR_MESSAGES: Record<string, string> = {
    'Invalid email or password':
        'Invalid email or password. Please try again.',
    'An account with this email already exists':
        'You already have an account. Please log in.',
    'Token has expired': 'This invite link has expired. Please ask your admin to resend the invitation.',
    'Token has already been used':
        'This invite has already been accepted. Try logging in instead.',
    'Token has been invalidated':
        'This invite has been revoked. Contact your admin.',
    'Token not found or invalid':
        'This invite link is invalid. Please ask your admin to resend the invitation.',
    'User account is suspended':
        'Your account has been suspended. Contact support.',
    'Multiple merchant memberships found. merchantId is required':
        'Your account has multiple organizations. Please select one.',
    'Direct dashboard login is not enabled for this merchant yet':
        'Dashboard login is not available yet. Please use Shopify to access your dashboard.',
    'Email not verified':
        'Please verify your email address before signing in. Check your inbox for a verification link.',
    'Verification token has expired':
        'This verification link has expired. Please request a new one.',
    'Verification token is invalid':
        'This verification link is invalid. Please request a new one.',
    'Email is already verified':
        'Your email is already verified. You can sign in now.',
    'Setup token expired or invalid. Please reinstall from Shopify.':
        'Your setup link has expired. Please reinstall Compra Surveys from the Shopify App Store.',
    'This account has been suspended':
        'This account has been suspended. Please contact support.',
    'This store is already connected to another account. Please contact support.':
        'This store is already connected to another account. Please contact support.',
};

/**
 * Maps a backend error message to a user-friendly string.
 * Falls back to the original message if no mapping exists.
 */
export const mapErrorMessage = (backendMessage: string): string => {
    // Check for exact match first
    if (ERROR_MESSAGES[backendMessage]) {
        return ERROR_MESSAGES[backendMessage];
    }
    // Check for partial match
    for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
        if (backendMessage.includes(key)) {
            return value;
        }
    }
    return backendMessage;
};

// ─── Password Validation ──────────────────────────────────────────
export const PASSWORD_REQUIREMENTS = {
    MIN_LENGTH: 8,
    REQUIRES_UPPERCASE: true,
    REQUIRES_LOWERCASE: true,
    REQUIRES_NUMBER: true,
    REQUIRES_SPECIAL: true,
} as const;
